/*
  KEVO contact form: inline validation messages in the store's language and a guard against
  double submission. The form itself is Shopify's native contact form; without JavaScript the
  browser's own `required` / `type=email` validation still applies.
*/
if (!customElements.get('kevo-contact-form')) {
  // Israeli ID number check digit (Luhn variant): up to 9 digits, padded with leading zeros.
  const isValidIsraeliId = (digits) => {
    if (!/^\d{5,9}$/.test(digits) || /^0+$/.test(digits)) return false;
    const id = digits.padStart(9, '0');
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      const n = Number(id[i]) * ((i % 2) + 1);
      sum += n > 9 ? n - 9 : n;
    }
    return sum % 10 === 0;
  };

  // Phone number: Israeli (0 + 8–9 digits, or a 10-digit 1-700/1-800 style number), +972, or any other
  // country code (+ or 00, 7–15 digits). Spaces, dashes, dots and brackets are ignored; the value is sent as typed.
  const isValidPhone = (value) => {
    if (!/^\+?[\d\s().-]+$/.test(value)) return false;
    let digits = value.replace(/[\s().-]/g, '');
    if (digits.startsWith('00')) digits = `+${digits.slice(2)}`;
    if (digits.startsWith('+972')) return /^0?[2-9]\d{7,8}$/.test(digits.slice(4));
    if (digits.startsWith('+')) return /^\+[1-9]\d{6,14}$/.test(digits);
    return /^0[2-9]\d{7,8}$/.test(digits) || /^1[5-9]\d{8}$/.test(digits);
  };

  customElements.define(
    'kevo-contact-form',
    class KevoContactForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        // Rendered by Shopify only after it accepted the submission.
        const success = this.form.querySelector('.kevo-contact__status--success');
        if (success) this.revealSuccess(success);
        this.submitButton = this.form.querySelector('[type="submit"]');
        this.submitLabel = this.submitButton ? this.submitButton.textContent : '';
        this.form.setAttribute('novalidate', '');

        // Capture phase, so this runs before Shopify's spam protection (hCaptcha), which adds its own submit
        // listener and then calls form.submit() regardless of preventDefault(). onSubmit stops the event from
        // reaching it whenever the form is invalid or already being sent.
        this.form.addEventListener('submit', (event) => this.onSubmit(event), true);

        // Pressing the submit button blurs the field being edited. Showing that field's error at that moment
        // pushes the button down before the press ends, so the click is lost. Blur validation is skipped for
        // that press only; the submit handler validates every field anyway.
        const trackSubmitPress = (event) => {
          this.pressingSubmit = !!this.submitButton && this.submitButton.contains(event.target);
        };
        document.addEventListener('pointerdown', trackSubmitPress, true);
        if (this.submitButton) this.submitButton.addEventListener('click', () => (this.pressingSubmit = false));

        this.form.querySelectorAll('input, textarea, select').forEach((field) => {
          field.addEventListener('input', () => {
            if (field.getAttribute('aria-invalid') === 'true') this.validateField(field);
          });
          field.addEventListener('blur', () => {
            if (field.value.trim() !== '' && !this.pressingSubmit) this.validateField(field);
          });
        });

        // Coming back with the browser's back button must not leave the button disabled.
        window.addEventListener('pageshow', () => this.setSubmitting(false));
      }

      // Shopify's success redirect adds #<form id>: browsers then ignore autofocus and leave the message at the
      // very top edge, where it can end up out of view or under the sticky header. Once the page has fully
      // loaded, focus the message without scrolling and centre it with a single scroll. Not on back/forward
      // navigation, where the browser restores the previous position.
      revealSuccess(message) {
        const navigation = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
        if (navigation && navigation.type === 'back_forward') return;
        const reveal = () =>
          requestAnimationFrame(() => {
            message.focus({ preventScroll: true });
            message.scrollIntoView({ block: 'center' });
          });
        if (document.readyState === 'complete') reveal();
        else window.addEventListener('load', reveal, { once: true });
      }

      messageFor(field) {
        const value = field.value.trim();
        if (field.required && value === '') {
          if (field.dataset.errorRequired) return field.dataset.errorRequired;
          if (field.type === 'email') return this.dataset.errorEmailRequired;
          if (field.tagName === 'TEXTAREA') return this.dataset.errorMessage;
          return this.dataset.errorName;
        }
        if (field.type === 'email' && value !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
          return this.dataset.errorEmailInvalid;
        }
        if (field.dataset.validate === 'il-id' && value !== '' && !isValidIsraeliId(value.replace(/[\s-]/g, ''))) {
          return field.dataset.errorInvalid;
        }
        if (field.dataset.validate === 'phone' && value !== '' && !isValidPhone(value)) {
          return field.dataset.errorInvalid;
        }
        return '';
      }

      validateField(field) {
        const message = this.messageFor(field);
        const errorId = (field.getAttribute('aria-describedby') || '').split(' ')[0];
        const error = errorId ? document.getElementById(errorId) : null;
        if (message) {
          field.setAttribute('aria-invalid', 'true');
        } else {
          field.removeAttribute('aria-invalid');
        }
        if (error) {
          error.textContent = message;
          error.hidden = !message;
        }
        return !message;
      }

      onSubmit(event) {
        if (this.submitting) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        let firstInvalid = null;
        this.form
          .querySelectorAll('input[required], textarea[required], select[required], input[type="email"], [data-validate]')
          .forEach((field) => {
            // ID numbers typed with spaces or dashes are sent as digits only.
            if (field.dataset.validate === 'il-id' && /^[\d\s-]+$/.test(field.value)) field.value = field.value.replace(/[\s-]/g, '');
            if (!this.validateField(field) && !firstInvalid) firstInvalid = field;
          });
        if (firstInvalid) {
          event.preventDefault();
          event.stopImmediatePropagation();
          firstInvalid.focus();
          return;
        }
        this.setSubmitting(true);
      }

      setSubmitting(state) {
        this.submitting = state;
        if (!this.submitButton) return;
        this.submitButton.toggleAttribute('aria-disabled', state);
        this.submitButton.classList.toggle('kevo-contact__submit--sending', state);
        this.submitButton.textContent = state && this.dataset.sendingLabel ? this.dataset.sendingLabel : this.submitLabel;
        this.form.setAttribute('aria-busy', state ? 'true' : 'false');
      }
    }
  );
}
